import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LanguageService } from '../../../services/i18n/language';
import { FileService, FileDto } from '../../../services/api/file';
import { Subscription } from 'rxjs';
import { ConfirmDialogService } from '../../../services/confirm-dialog';

/**
 * Договор между грузовладельцем и поставщиком: обе стороны загружают свою подписанную
 * копию под один и тот же контейнер SUPPLIER_CONTRACT (containerId = ID расчёта).
 * Различаем «чья копия» через documentCategory (OWNER_SIGNED / SUPPLIER_SIGNED),
 * выставляемый при загрузке в зависимости от роли текущего пользователя.
 */
@Component({
  selector: 'app-contract-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './contract-upload.html',
  styleUrl: './contract-upload.scss'
})
export class ContractUploadComponent implements OnInit, OnDestroy {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);

  @Input() calculationId!: number;
  @Input() role: 'OWNER' | 'SUPPLIER' = 'OWNER';

  files: FileDto[] = [];
  loading = false;
  uploading = false;
  error: string | null = null;

  titleText = 'Договор между грузовладельцем и поставщиком';
  hintText = 'Каждая сторона загружает свою подписанную копию.';
  loadingText = 'Загрузка...';
  emptyStateText = 'Договор пока не загружен ни одной из сторон.';
  uploadButtonText = 'Загрузить подписанный договор';
  uploadingText = 'Загрузка...';
  categoryOwnerSignedText = 'Подписано грузовладельцем';
  categorySupplierSignedText = 'Подписано поставщиком';
  categoryFileText = 'Файл';
  confirmDeleteText = 'Удалить файл?';

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private fileService: FileService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.titleText = this.t('contractUpload.title', 'Договор между грузовладельцем и поставщиком');
    this.hintText = this.t('contractUpload.hint', 'Каждая сторона загружает свою подписанную копию.');
    this.loadingText = this.t('contractUpload.loading', 'Загрузка...');
    this.emptyStateText = this.t('contractUpload.emptyState', 'Договор пока не загружен ни одной из сторон.');
    this.uploadButtonText = this.t('contractUpload.uploadButton', 'Загрузить подписанный договор');
    this.uploadingText = this.t('contractUpload.uploading', 'Загрузка...');
    this.categoryOwnerSignedText = this.t('contractUpload.categoryOwnerSigned', 'Подписано грузовладельцем');
    this.categorySupplierSignedText = this.t('contractUpload.categorySupplierSigned', 'Подписано поставщиком');
    this.categoryFileText = this.t('contractUpload.categoryFile', 'Файл');
    this.confirmDeleteText = this.t('contractUpload.confirmDelete', 'Удалить файл?');
  }

  private load(): void {
    if (!this.calculationId) return;
    this.loading = true;
    this.fileService.getFilesByContainer('SUPPLIER_CONTRACT', this.calculationId).subscribe({
      next: (files) => {
        this.files = files;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading contract files:', err);
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.uploading = true;
    this.error = null;
    const category = this.role === 'OWNER' ? 'OWNER_SIGNED' : 'SUPPLIER_SIGNED';

    this.fileService.uploadFile(file, 'SUPPLIER_CONTRACT', this.calculationId, category).subscribe({
      next: () => {
        this.uploading = false;
        input.value = '';
        this.load();
      },
      error: (err) => {
        console.error('Error uploading contract file:', err);
        this.error = this.t('contractUpload.errorUpload', 'Ошибка при загрузке файла');
        this.uploading = false;
        this.cdr.markForCheck();
      }
    });
  }

  async deleteFile(file: FileDto): Promise<void> {
    if (!await this.confirmDialog.confirm(this.confirmDeleteText)) return;
    this.fileService.deleteFile(file.uid).subscribe({
      next: () => this.load(),
      error: (err) => console.error('Error deleting contract file:', err)
    });
  }

  downloadFile(file: FileDto): void {
    this.fileService.downloadFile(file.uid, file.fileName);
  }

  categoryLabel(file: FileDto): string {
    if (file.documentCategory === 'OWNER_SIGNED') return this.categoryOwnerSignedText;
    if (file.documentCategory === 'SUPPLIER_SIGNED') return this.categorySupplierSignedText;
    return this.categoryFileText;
  }
}
